/**
 * Deep links into a due diligence report: `/due-diligence/case/<alias>/report#block-3-3`.
 *
 * The hash is the DOM id the report already gives a section (`section-3`) or a
 * block (`block-3-3`), so the URL reads the same as the page it points to. Its
 * numbers are the canonical ones — what the stored report and the chatbot use —
 * not the display numbers a tier can renumber the sections to. A block's number
 * carries its section, so one hash is enough for both levels.
 */

export interface ReportAnchor {
  // Canonical section number, e.g. "3".
  sectionNumber: string;
  // The block number past its section, e.g. ".3" for block 3.3.
  blockSuffix?: string;
}

/**
 * @param caseAlias The case alias (or id) the report URL is built on.
 * @returns The path of the case's final report.
 */
export const reportPath = (caseAlias: string): string =>
  `/due-diligence/case/${caseAlias}/report`;

/**
 * @param blockNumber A block number as the page renders it, e.g. "3.3".
 * @returns The DOM id the report gives that block.
 */
export const blockDomId = (blockNumber: string): string =>
  `block-${String(blockNumber).replace(/\./g, "-")}`;

/**
 * @param sectionNumber Canonical section number.
 * @param blockNumber Canonical block number, when the link points at a block.
 * @returns The hash that opens that section, or that block inside it.
 */
export const reportAnchorHash = (sectionNumber: string, blockNumber?: string): string =>
  `#${blockNumber ? blockDomId(blockNumber) : `section-${sectionNumber}`}`;

/**
 * @param hash `location.hash`, with its leading "#".
 * @returns The section and block it points at, or null when it is not a report anchor.
 */
export const parseReportAnchor = (hash: string): ReportAnchor | null => {
  const section = /^#section-(\d+)$/.exec(hash);
  if (section) return { sectionNumber: section[1] };

  const block = /^#block-(\d+)((?:-\d+)+)$/.exec(hash);
  if (block) return { sectionNumber: block[1], blockSuffix: block[2].replace(/-/g, ".") };

  return null;
};

/**
 * The page renders a block under its section's display number ("14.2" under a §14
 * shown as §7 is "7.2"), so the DOM id is built on that number, not the canonical one.
 *
 * @param blockSuffix The anchor's block suffix, e.g. ".2".
 * @param displayNumber The number the block's section is displayed with.
 * @returns The DOM id of the block on the page.
 */
export const anchorBlockDomId = (blockSuffix: string, displayNumber: string): string =>
  blockDomId(`${displayNumber}${blockSuffix}`);
